import React, { useState, useEffect } from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { Grid } from "@material-ui/core";
import { TestHarnessCashflowModal } from "modals";
import { Formik } from "formik";
import PageWrapper from "components/Global/PageOrganization/PageWrapper";
import "./styles.css";
import { calculateRefiBreakEven } from "store/state/calculators/testHarness";
import PrepaymentType from "components/Calculators/Constants/PrepaymentType";

import { treasurySnapshotGetHistoricalDataBySymbol, treasurySnapshotGetLatestRateBySymbol } from "../../../modals/TestHarnessCashflowModal/treasurySnapshot.store";
import { TREASURY_YEAR_TYPE } from "../Constants/Constants";

import {
  Header,
  CalculationPillBox,
  ExistingFinancingForm,
  NewFinancingForm,
  DealSearchField,
  LoanProceedsTooLowBanner
} from "./";

const TestHarness = props => {
  const {
    calculateRefiBreakEven,
    treasurySnapshotGetHistoricalDataBySymbol,
    treasurySnapshotGetLatestRateBySymbol,
    breakEvenResults,
    deal
  } = props;

  const [cashflowModalIsVisible, setCashFlowModalIsVisible] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [dealId, setDealId] = useState(null);

  const loanProceedAssumptions = deal?.[dealId]?.loanProceedAssumptions?.[0];

  const {
    loanType: dealLoanType = "",
    rateType: dealRateType = "",
    index: dealRateIndex = "",
    loanSpread: dealSpread = "",
    minDSCR: dealMinDscr = "",
    maxLTV: dealMaxLtv = "",
    minDebtYield: dealMinDebtYield = "",
    term: dealLoanTerm = "",
    dayCount: dealDaycount = "",
    closingCosts: dealClosingCosts = "",
    amortizationType: dealAmortizationType = "",
    amortizationTerm: dealAmortizationTerm = "",
    amoritzationRate: dealAmortizationRate = ""
  } = loanProceedAssumptions || {};

  useEffect(() => {
    treasurySnapshotGetLatestRateBySymbol({ symbol: TREASURY_YEAR_TYPE.TEASURY_Y10 });
    treasurySnapshotGetHistoricalDataBySymbol({ symbol: TREASURY_YEAR_TYPE.TEASURY_Y10 });
  }, []);

  useEffect(() => {
    if (loanProceedAssumptions) {
      setDealValues({
        ...initialEfValues,
        nfLoanType: dealLoanType,
        nfRateType: dealRateType?.name.toUpperCase(),
        nfRateIndex: dealRateIndex?.name,
        nfSpread: dealSpread,
        nfNoi: "",
        nfCapRate: "",
        nfMinDscr: dealMinDscr,
        nfMaxLtv: dealMaxLtv,
        nfMinDebtYield: dealMinDebtYield,
        nfStartDate: new Date(),
        nfLoanTerm: dealLoanTerm,
        nfDaycount: dealDaycount?.name,
        nfClosingCosts: dealClosingCosts,
        nfAmortizationType: dealAmortizationType?.name,
        nfAmortizationTerm: dealAmortizationTerm,
        nfIoPeriods: "",
        nfAmortizationRate: dealAmortizationRate
      });
    } else if (!deal || !loanProceedAssumptions) {
      setDealValues(initialValues);
    }
  }, [deal]);

  const handleCashflowModalVisibility = visible => setCashFlowModalIsVisible(visible);
  const handleCloseModal = () => setCashFlowModalIsVisible(false);

  // This is for quickly instantiating values to get cashflow calcs.
  const sampleInitialValues = {
    efLoanAmount: "25000000",
    efStartDate: new Date("06/01/2015"),
    efLoanTerm: "120",
    efDaycount: "ACTUAL_OVER_360",
    efRateType: "FIXED",
    efRate: "5.5",
    efPrepaymentType: "YIELD_MAINTENANCE_TRADITIONAL",
    efAmortizationTerm: "300",
    efIoPeriods: "0",
    efOpenPeriods: "0",
    efMinimumPenalty: "1",
    efAmortizationType: "MORTGAGE_STYLE",
    nfLoanType: 4, // Balance Sheet
    nfRateType: "FIXED",
    nfRateIndex: "internallyFixed",
    nfRate: "5",
    nfNoi: "10000000",
    nfCapRate: "10",
    nfMinDscr: "1",
    nfMaxLtv: "1",
    nfMinDebtYield: "1",
    nfStartDate: new Date("Wed Mar 31 2021 00:00:00 GMT-0400 (Eastern Daylight Time)"),
    nfLoanTerm: "120",
    nfDaycount: "THIRTY_OVER_360",
    nfClosingCosts: "1",
    nfAmortizationType: "MORTGAGE_STYLE",
    nfAmortizationTerm: "360",
    efRateIndex: "",
    efLoanSpread: "",
    efFlatFeePercentage: "",
    efAggregateInterestMethod: "",
    efAggregateInterestAmount: "",
    efAggregateInterestMonthsOfInterest: "",
    nfSpread: "",
    nfIoPeriods: "0",
    nfAmortizationRate: "",
    dealId: ""
  };

  const initialEfValues = {
    efLoanAmount: "",
    efStartDate: "",
    efLoanTerm: "",
    efDaycount: "",
    efRateType: "",
    efRate: "",
    efRateIndex: "",
    efLoanSpread: "",
    efPrepaymentType: "",
    efOpenPeriods: "",
    efMinimumPenalty: "",
    efFlatFeePercentage: "",
    efAggregateInterestMethod: "",
    efAggregateInterestAmount: "",
    efAggregateInterestMonthsOfInterest: "",
    efAmortizationType: "",
    efAmortizationTerm: "",
    efIoPeriods: ""
  };

  const initialNfValues = {
    nfLoanType: "",
    nfRateType: "",
    nfRateIndex: "",
    nfSpread: "",
    nfRate: "",
    nfNoi: "",
    nfCapRate: "",
    nfMinDscr: "",
    nfMaxLtv: "",
    nfMinDebtYield: "",
    nfStartDate: "",
    nfLoanTerm: "",
    nfDaycount: "",
    nfClosingCosts: "",
    nfAmortizationType: "",
    nfAmortizationTerm: "",
    nfIoPeriods: "",
    nfAmortizationRate: ""
  };

  const initialValues = {
    dealId: "",
    ...initialEfValues,
    ...initialNfValues
  };

  const [dealValues, setDealValues] = useState(initialValues);

  const validateForm = values => {
    setSubmitted(true);

    return Object.keys(values).reduce((acc, fieldName) => {
      if (fieldName === "dealId") {
        return acc;
      }

      if (values.dealId) {
        if (fieldName.startsWith("ef")) {
          return acc;
        }
      }

      if (fieldName === "nfAmortizationRate") {
        if (values.nfRateType !== "FIXED") {
          return acc;
        }
      }

      if (fieldName === "nfSpread") {
        if (values.nfRateIndex === "internallyFixed") {
          return acc;
        }
      }

      if (fieldName === "nfRate") {
        if (values.nfRateIndex === "treasury" || values.nfRateIndex === "semiBondSwaps") {
          return acc;
        }
      }

      if (fieldName === "efRate") {
        if (values.efRateType === "FLOATING") {
          return acc;
        }
      }

      if (fieldName === "efLoanSpread" || fieldName === "efRateIndex") {
        if (values.efRateType === "FIXED") {
          return acc;
        }
      }

      if (fieldName === "nfIoPeriods") {
        if (values.nfAmortizationType === "INTEREST_ONLY") {
          return acc;
        }
      }

      if (fieldName === "efIoPeriods") {
        if (values.efAmortizationType === "INTEREST_ONLY") {
          return acc;
        }
      }

      if (fieldName === "nfAmortizationRate") {
        if (values.nfRateType === "FIXED") {
          return acc;
        }
      }

      if (fieldName === "efAggregateInterestMonthsOfInterest") {
        if (values.efAggregateInterestMethod === "DEFINED" || values.efAggregateInterestMethod === "") {
          return acc;
        }
      }

      if (fieldName === "efAggregateInterestAmount") {
        if (values.efAggregateInterestMethod === "FORMULAIC" || values.efAggregateInterestMethod === "") {
          return acc;
        }
      }

      if (fieldName === "efOpenPeriods" || fieldName === "efAggregateInterestMethod" ||
				fieldName === "efMinimumPenalty") {
        if (values.efPaymentType === PrepaymentType.FLAT_PERCENTAGE) {
          return acc;
        }
      }

      if (fieldName === "efMinimumPenalty" || fieldName === "efOpenPeriods" || fieldName === "efFlatFeePercentage") {
        if (values.efPaymentType === PrepaymentType.AGGREGATE_INTEREST) {
          return acc;
        }
      }

      if (fieldName === "efAggregateInterestMethod" || fieldName === "efMinimumPenalty" || fieldName === "efFlatFeePercentage") {
        if (values.efPrepaymentType === PrepaymentType.DEFEASANCE ||
					values.efPrepaymentType === PrepaymentType.SPREAD_MAINTENANCE) {
          return acc;
        }
      }

      if (fieldName === "efFlatFeePercentage" || fieldName === "efAggregateInterestMethod") {
        if (values.efPrepaymentType === PrepaymentType.MAKE_WHOLE ||
					values.efPrepaymentType === PrepaymentType.YIELD_MAINTENANCE_AGENCY_STYLE ||
					values.efPrepaymentType === PrepaymentType.YIELD_MAINTENANCE_TRADITIONAL) {
          return acc;
        }
      }

      if (!values[fieldName]) {
        acc[fieldName] = `${fieldName} is required`;
      }

      return {};
    }, {});
  };

  const handleSubmission = values => {
    const form = {
      dealId: values.dealId,
      existingFinancing: {
        rate: values.efRate / 100,
        rateIndex: values.efRateIndex,
        loanSpread: values.efLoanSpread / 100,
        loanAmount: values.efLoanAmount,
        startDate: values.efStartDate,
        loanTerm: values.efLoanTerm,
        rateType: values.efRateType,
        prepaymentType: values.efPrepaymentType,
        openPeriods: values.efOpenPeriods,
        minimumPenalty: values.efMinimumPenalty / 100,
        aggregateInterestMethod: values.efAggregateInterestMethod,
        aggregateInterestAmount: values.efAggregateInterestAmount,
        aggregateInterestMonthsOfInterest: values.efAggregateInterestMonthsOfInterest,
        flatFeePercentage: values.efFlatFeePercentage / 100,
        dayCount: values.efDaycount,
        amortizationType: values.efAmortizationType,
        amortizationTerm: values.efAmortizationTerm,
        ioPeriods: values.efIoPeriods
      },
      newFinancing: {
        loanType: values.nfLoanType,
        rateType: values.nfRateType,
        spread: values.nfSpread / 100,
        rate: values.nfRate / 100,
        noi: values.nfNoi,
        capRate: values.nfCapRate / 100,
        minDscr: values.nfMinDscr,
        maxLtv: values.nfMaxLtv / 100,
        minDebtYield: values.nfMinDebtYield / 100,
        startDate: values.nfStartDate,
        loanTerm: values.nfLoanTerm,
        rateIndex: values.nfRateIndex,
        dayCount: values.nfDaycount,
        closingCosts: values.nfClosingCosts / 100,
        amortizationType: values.nfAmortizationType,
        amortizationTerm: values.nfAmortizationTerm,
        amortizationRate: values.nfAmortizationRate / 100,
        interestOnlyPeriods: values.nfIoPeriods
      }
    };
    calculateRefiBreakEven(form);
  };

  return (
  // <Template {...props} refiType='Refi Breakeven' />
    <Formik
      initialValues={initialValues}
      onReset={(initialValues, form) => {
        setSubmitted(false); form.resetForm(initialValues);
      }}
      onSubmit={(values => {
        handleSubmission(values);
      })}
      validate={validateForm}
      validateOnChange={submitted}
      validateOnBlur={false}
      render={formikProps => (
        <div className="test-harness-wrapper">
          <TestHarnessCashflowModal
            {...formikProps}
            calculations={breakEvenResults}
            open={cashflowModalIsVisible}
            handleClose={handleCloseModal}
          />
          <PageWrapper>
            <Grid container direction="column" style={{ maxWidth: "1300px" }}>
              <Grid item><Header {...formikProps} handleCashflowModalVisibility={() => handleCashflowModalVisibility(!cashflowModalIsVisible)} setDealId={setDealId} /></Grid>
              {breakEvenResults?.error && submitted && <Grid item><LoanProceedsTooLowBanner breakEvenResults={breakEvenResults} /></Grid>}
              {breakEvenResults && submitted && !breakEvenResults.error && <Grid container item alignItems="center"><CalculationPillBox {...formikProps} calculations={breakEvenResults} showCashflows={() => handleCashflowModalVisibility(true)} cashflowModalIsVisible /></Grid>}
              <Grid item><DealSearchField {...formikProps} setDealId={setDealId} setSubmitted={setSubmitted} /></Grid>
              {(!deal || !loanProceedAssumptions) && <Grid item><ExistingFinancingForm {...formikProps} /></Grid>}
              <Grid item><NewFinancingForm {...formikProps} dealValues={dealValues} dealId={dealId} /></Grid>
            </Grid>
          </PageWrapper>
        </div>
      )}
    />
  );
};

const mapStateToProps = ({ testHarness, dealSettings }) => ({
  breakEvenResults: testHarness.refiBreakEvenResults.data,
  ...dealSettings
});

export default compose(connect(mapStateToProps, { calculateRefiBreakEven, treasurySnapshotGetHistoricalDataBySymbol, treasurySnapshotGetLatestRateBySymbol }))(TestHarness);
